import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const DisputeOverviewCard = ({ dispute, statusConfig }) => {
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h2 className="font-heading text-2xl font-bold text-foreground">
              {dispute?.title}
            </h2>
            <div className={`flex items-center space-x-1 px-3 py-1 rounded-full ${statusConfig?.color}`}>
              <Icon name={statusConfig?.icon} size={14} strokeWidth={2} />
              <span className="font-caption text-xs font-semibold">{statusConfig?.label}</span>
            </div>
          </div>
          <p className="font-caption text-sm text-muted-foreground">
            Case #{dispute?.caseNumber} • Filed {dispute?.createdDate}
          </p>
        </div>
      </div>

      {/* Disputed Amount */}
      <div className="bg-error/5 border border-error/20 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-caption text-xs text-muted-foreground mb-1">Contested Amount</p>
            <p className="font-heading text-3xl font-bold text-error">${dispute?.contestedAmount}</p>
          </div>
          <div className="text-right">
            <p className="font-caption text-xs text-muted-foreground mb-1">Original Charge</p>
            <p className="font-body text-lg font-semibold text-foreground">${dispute?.originalCharge}</p>
          </div>
        </div>
      </div>

      {/* Trailer & Renter Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Trailer Info */}
        <div className="bg-muted/30 rounded-lg p-4">
          <h3 className="font-body font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Icon name="Package" size={18} strokeWidth={2} />
            <span>Trailer Information</span>
          </h3>
          <div className="flex items-center space-x-3">
            <div className="w-16 h-16 rounded overflow-hidden flex-shrink-0">
              <Image
                src={dispute?.trailerImage}
                alt={dispute?.trailerImageAlt}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="font-body font-semibold text-foreground">{dispute?.trailerName}</p>
              <p className="font-caption text-xs text-muted-foreground">Your Trailer</p>
            </div>
          </div>
        </div>

        {/* Renter Info */}
        <div className="bg-muted/30 rounded-lg p-4">
          <h3 className="font-body font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Icon name="User" size={18} strokeWidth={2} />
            <span>Renter Information</span>
          </h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="font-body font-semibold text-foreground">{dispute?.renterName}</p>
              <div className="flex items-center space-x-1">
                <Icon name="Star" size={14} className="text-warning" fill="currentColor" strokeWidth={0} />
                <span className="font-body text-sm font-semibold text-foreground">{dispute?.renterRating}</span>
              </div>
            </div>
            <p className="font-caption text-xs text-muted-foreground">{dispute?.renterEmail}</p>
          </div>
        </div>
      </div>

      {/* Dispute Description */}
      <div>
        <h3 className="font-body font-semibold text-foreground mb-2">Dispute Reason</h3>
        <p className="font-body text-foreground leading-relaxed">{dispute?.description}</p>
      </div>

      {/* Renter's Evidence Summary */}
      <div>
        <h3 className="font-body font-semibold text-foreground mb-3 flex items-center space-x-2">
          <Icon name="Image" size={18} strokeWidth={2} />
          <span>Renter's Evidence ({dispute?.renterEvidence?.length})</span>
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {dispute?.renterEvidence?.map((evidence) => (
            <div key={evidence?.id} className="bg-muted/30 rounded-lg p-3 border border-border">
              {evidence?.type === 'image' ? (
                <img
                  src={evidence?.url}
                  alt={evidence?.alt}
                  className="w-full h-24 object-cover rounded mb-2"
                />
              ) : (
                <div className="w-full h-24 bg-primary/10 rounded flex items-center justify-center mb-2">
                  <Icon
                    name={evidence?.type === 'video' ? 'Video' : 'FileText'}
                    size={32}
                    className="text-primary"
                    strokeWidth={2}
                  />
                </div>
              )}
              <p className="font-caption text-xs text-foreground truncate">{evidence?.name}</p>
              <p className="font-caption text-xs text-muted-foreground">{evidence?.timestamp}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Timeline */}
      <div>
        <h3 className="font-body font-semibold text-foreground mb-4 flex items-center space-x-2">
          <Icon name="Clock" size={18} strokeWidth={2} />
          <span>Timeline</span>
        </h3>
        <div className="space-y-4">
          {dispute?.timeline?.map((event, index) => (
            <div key={index} className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-2 h-2 rounded-full bg-primary mt-2"></div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <p className="font-body font-semibold text-foreground">{event?.action}</p>
                  <p className="font-caption text-xs text-muted-foreground">
                    {formatTimestamp(event?.date)}
                  </p>
                </div>
                <p className="font-body text-sm text-muted-foreground">
                  {event?.actor}: {event?.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Response Deadline Warning */}
      {dispute?.status === 'awaiting_owner_response' && (
        <div className="bg-warning/10 border border-warning rounded-lg p-4 flex items-start space-x-3">
          <Icon name="AlertCircle" size={24} className="text-warning flex-shrink-0" strokeWidth={2} />
          <div>
            <p className="font-body font-semibold text-warning mb-1">Response Required</p>
            <p className="font-body text-sm text-foreground">
              Please respond to this dispute by <span className="font-semibold">{dispute?.responseDeadline}</span> to avoid automatic escalation to mediation.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisputeOverviewCard;